import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import { malaysiaScraping, type SsmLoginResponse } from './malaysiaScraping';

export type { SsmLoginResponse };

/**
 * Beneficial Owner API
 * SSM4U 실소유자 등록 업무 전용 API
 *
 * 엔드포인트: /api/marketplace/v1/malaysia/ssm4u/beneficial-owners/*
 */

export interface AppointBORequest {
  credentialId: string;
  sessionId: string;
  uniqueEntityNumber: string;
  boFormData: any; // BO form data from BeneficialOwnerForm
}

export interface AppointBOGroupRequest {
  credentialId: string;
  sessionId: string;
  uniqueEntityNumber: string;
  groupData: any; // Group form data from BeneficialOwnerGroupForm
}

export interface BOAppointResponse {
  success: boolean;
  error?: string;
  pdfDataBase64?: string;
}

export interface BOListResponse {
  success: boolean;
  error?: string;
  unique_entity_number?: string;
  entity_name?: string;
  beneficial_owners?: any[];
}

/**
 * Date format conversion: any format → "YYYY-MM-DD" (backend expects strptime "%Y-%m-%d")
 */
const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

function toISODate(dateStr?: string): string {
  if (!dateStr) return '';
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // dd/MM/yyyy → YYYY-MM-DD
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [d, m, y] = dateStr.split('/');
    return `${y}-${m}-${d}`;
  }
  // DD MMMM YYYY
  const match = dateStr.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTH_NAMES.indexOf(match[2]);
    if (monthIdx >= 0) {
      return `${match[3]}-${String(monthIdx + 1).padStart(2, '0')}-${match[1].padStart(2, '0')}`;
    }
  }
  return dateStr;
}

/**
 * Enum 매핑: Frontend 짧은 코드 → Backend Python Enum 키
 *
 * Backend: BeneficialOwnerType[bo_req.bo_type] 로 조회하므로
 * Python Enum 멤버 이름(key)을 정확히 보내야 함
 */
const BO_TYPE_MAP: Record<string, string> = {
  'I': 'INDIVIDUAL',
  'T': 'TRUSTEE',
  'FGA': 'FEDERAL_GOVERNMENT_AGENCY',
  'SG': 'STATE_GOVERNMENT',
  'FG': 'FEDERAL_GOVERNMENT',
  'NPO': 'NON_PROFIT_ORGANIZATION',
  'UB': 'UNINCORPORATED_BODY',
  'AG': 'AGENCY',
  'OTH': 'OTHERS',
};

const CATEGORY_MAP: Record<string, string> = {
  'I': 'INDIVIDUAL',
  'E': 'EXECUTOR',
  'ABD': 'ADMINISTRATOR_ON_BEHALF_OF_DECEASED',
  'ABBP': 'ADMINISTRATOR_ON_BEHALF_OF_BANKRUPT_PERSON',
};

const CITIZENSHIP_MAP: Record<string, string> = {
  'M': 'MALAYSIAN',
  'NM': 'NON_MALAYSIAN',
  'PR': 'PERMANENT_RESIDENT',
};

const TYPES_OF_BO_MAP: Record<string, string> = {
  'D': 'DIRECT_OWNERSHIP',
  'I': 'INDIRECT_OWNERSHIP',
  'DI': 'DIRECT_INDIRECT_OWNERSHIP',
  'C': 'CONTROL_BY_OTHER_MEANS',
};

const CRITERION_MAP: Record<string, string> = {
  'A': 'CRITERIA_A',
  'B': 'CRITERIA_B',
  'C': 'CRITERIA_C',
  'D': 'CRITERIA_D',
  'E': 'CRITERIA_E',
  'F': 'CRITERIA_F',
};

/**
 * Nationality name → ISO 3166-1 alpha-3 code
 * Backend SSM4U expects 3-letter codes (e.g., MYS, SGP)
 */
const NATIONALITY_ISO_MAP: Record<string, string> = {
  'MALAYSIA': 'MYS', 'SINGAPORE': 'SGP', 'INDONESIA': 'IDN', 'CHINA': 'CHN',
  'INDIA': 'IND', 'THAILAND': 'THA', 'PHILIPPINES': 'PHL', 'VIETNAM': 'VNM',
  'MYANMAR': 'MMR', 'CAMBODIA': 'KHM', 'JAPAN': 'JPN', 'KOREA_SOUTH': 'KOR',
  'TAIWAN': 'TWN', 'HONG_KONG': 'HKG', 'BANGLADESH': 'BGD', 'PAKISTAN': 'PAK',
  'SRI_LANKA': 'LKA', 'NEPAL': 'NPL', 'UNITED_KINGDOM': 'GBR',
  'UNITED_STATES_OF_AMERICA': 'USA', 'AUSTRALIA': 'AUS', 'NEW_ZEALAND': 'NZL',
  'CANADA': 'CAN', 'GERMANY': 'DEU', 'FRANCE': 'FRA', 'NETHERLANDS': 'NLD',
  'ITALY': 'ITA', 'SPAIN': 'ESP', 'SWITZERLAND': 'CHE',
  'UNITED_ARAB_EMIRATES': 'ARE', 'SAUDI_ARABIA': 'SAU', 'QATAR': 'QAT',
  'BAHRAIN': 'BHR', 'KUWAIT': 'KWT', 'BRUNEI': 'BRN', 'LAOS': 'LAO',
  'TIMOR_LESTE': 'TLS', 'SOUTH_AFRICA': 'ZAF', 'NIGERIA': 'NGA', 'EGYPT': 'EGY',
  'BRAZIL': 'BRA', 'MEXICO': 'MEX', 'ARGENTINA': 'ARG', 'RUSSIA': 'RUS',
  'TURKEY': 'TUR', 'IRAN': 'IRN',
};

/**
 * Beneficial Owner API
 */
export const beneficialOwnerApi = {
  /**
   * IDPRO 로그인 (SSM4U)
   */
  idproLogin: (credentialId: string): Promise<SsmLoginResponse> => {
    return malaysiaScraping.ssmLogin(credentialId, 'idpro');
  },

  /**
   * IDPRO 로그아웃
   */
  idproLogout: (sessionId: string) => {
    return malaysiaScraping.ssmLogout(sessionId);
  },

  /**
   * BO 목록 조회
   */
  getBOList: async (credentialId: string, sessionId: string, uniqueEntityNumber: string): Promise<ApiResponse<BOListResponse>> => {
    const response = await apiClient.post<BOListResponse>('/marketplace/v1/malaysia/ssm4u/beneficial-owners/list', {
      credential_id: credentialId,
      session_id: sessionId,
      unique_entity_number: uniqueEntityNumber,
    });
    return response;
  },

  /**
   * Add Beneficial Owner (등록)
   *
   * Backend Model: AppointBeneficialOwnerRequest → BeneficialOwnerRequest
   * - 필드명: snake_case (identification_type, person_name 등)
   * - 날짜: YYYY-MM-DD (strptime "%Y-%m-%d")
   * - Enum: Python Enum 키 (INDIVIDUAL, DIRECT_OWNERSHIP 등)
   * - Address: BeneficialOwnerAddress (street01, post_code 등)
   * - Criteria: CriterionRequest (criterion=CRITERIA_C, voting_rights 등)
   */
  appointBO: async (data: AppointBORequest): Promise<ApiResponse<BOAppointResponse>> => {
    console.log('[BeneficialOwnerApi] Appoint BO request:', {
      uniqueEntityNumber: data.uniqueEntityNumber,
      boName: data.boFormData?.name,
    });

    const formData = data.boFormData;

    const requestData = {
      credential_id: data.credentialId,
      session_id: data.sessionId,
      unique_entity_number: data.uniqueEntityNumber,
      beneficial_owners: [
        {
          // Person info (Backend: BeneficialOwnerRequest fields)
          identification_type: formData.idType || '',
          identification_number: formData.idNo || '',
          person_name: formData.name || '',
          date_of_birth: toISODate(formData.dob),
          date_of_becoming_bo: toISODate(formData.changeDt),
          date_of_data_recorded: toISODate(formData.recordedDt),
          email: formData.email || '',
          phone_number: formData.phoneNo || '',
          designation: formData.designation || '',
          gender: formData.gender || '',
          race: formData.race || '',

          // Enums (mapped to Python Enum keys)
          bo_type: BO_TYPE_MAP[formData.boType] || formData.boType || 'NA',
          category: CATEGORY_MAP[formData.category] || formData.category || 'NA',
          citizenship: CITIZENSHIP_MAP[formData.citizenship] || formData.citizenship || 'NA',
          nationality: NATIONALITY_ISO_MAP[formData.nationality] || formData.nationality || '',
          types_of_bo: TYPES_OF_BO_MAP[formData.typeBo] || formData.typeBo || 'NA',

          // Booleans
          joint_arrangement: formData.isJointArrangement === 'true',
          joint_shareholder: formData.isJointShareholder === 'true',

          // Criteria (CriterionRequest)
          criteria: buildCriteriaPayload(formData),

          // Addresses (BeneficialOwnerAddress)
          residential_address: {
            is_local: !!formData.resIsLocal,
            street01: formData.resAdd1 || '',
            street02: formData.resAdd2 || '',
            street03: formData.resAdd3 || '',
            city: formData.resIsLocal ? (formData.resCity || '') : (formData.resCityOverseas || ''),
            state: formData.resIsLocal ? (formData.resState || '') : (formData.resStateOverseas || ''),
            post_code: formData.resIsLocal ? (formData.resPostcode || '') : '',
            country: formData.resIsLocal ? 'MALAYSIA' : (formData.resCountry || ''),
          },
          // Business address: same-as-res → copy residential, has data → use biz fields, empty → null
          business_address: formData.bizSameAsRes
            ? {
                is_local: !!formData.resIsLocal,
                street01: formData.resAdd1 || '',
                street02: formData.resAdd2 || '',
                street03: formData.resAdd3 || '',
                city: formData.resIsLocal ? (formData.resCity || '') : (formData.resCityOverseas || ''),
                state: formData.resIsLocal ? (formData.resState || '') : (formData.resStateOverseas || ''),
                post_code: formData.resIsLocal ? (formData.resPostcode || '') : '',
                country: formData.resIsLocal ? 'MALAYSIA' : (formData.resCountry || ''),
              }
            : (formData.bizAdd1?.trim()
              ? {
                  is_local: !!formData.bizIsLocal,
                  street01: formData.bizAdd1 || '',
                  street02: formData.bizAdd2 || '',
                  street03: formData.bizAdd3 || '',
                  city: formData.bizIsLocal ? (formData.bizCity || '') : '',
                  state: formData.bizIsLocal ? (formData.bizState || '') : '',
                  post_code: formData.bizIsLocal ? (formData.bizPostcode || '') : '',
                  country: formData.bizIsLocal ? 'MALAYSIA' : (formData.bizCountry || ''),
                }
              : null),
        },
      ],
    };

    const response = await apiClient.post<BOAppointResponse>(
      '/marketplace/v1/malaysia/ssm4u/beneficial-owners/appoint',
      requestData,
    );

    console.log('[BeneficialOwnerApi] Appoint BO response:', {
      success: (response as any)?.success || (response as any)?.data?.success,
    });

    return response;
  },

  /**
   * Add Beneficial Owner Group (그룹 등록)
   * - 등록된 BO 2명 이상 선택하여 그룹으로 묶기
   * - 선택 사항(optional)
   */
  appointBOGroup: async (data: AppointBOGroupRequest): Promise<ApiResponse<BOAppointResponse>> => {
    console.log('[BeneficialOwnerApi] Appoint BO Group request:', {
      uniqueEntityNumber: data.uniqueEntityNumber,
      selectedBOs: data.groupData?.selectedBOIds?.length,
    });

    const requestData = {
      credential_id: data.credentialId,
      session_id: data.sessionId,
      unique_entity_number: data.uniqueEntityNumber,
      selected_bo_ids: data.groupData?.selectedBOIds || [],
    };

    const response = await apiClient.post<BOAppointResponse>(
      '/marketplace/v1/malaysia/ssm4u/beneficial-owners/group/appoint',
      requestData,
    );

    return response;
  },
};

/**
 * Build criteria payload from form data
 * Backend: CriterionRequest { criterion, direct, indirect, voting_shares, voting_rights }
 *
 * Logic (from SSM4U beneficial-owner-test.html):
 * - D  → CRITERIA_A with direct = directPercent
 * - I  → CRITERIA_B with indirect = indirectPercent
 * - DI → CRITERIA_A(direct) + CRITERIA_B(indirect)
 * - C or Joint=Yes → CRITERIA_C/D/E/F based on checkboxes
 */
function buildCriteriaPayload(formData: any): any[] {
  const criteria: any[] = [];
  const typeBo = formData.typeBo || '';

  // D/I/DI: Auto-map to CRITERIA_A (direct/indirect) and CRITERIA_B (voting shares)
  if (formData.critboA && (typeBo === 'D' || typeBo === 'I' || typeBo === 'DI')) {
    criteria.push({
      criterion: CRITERION_MAP['A'],
      direct: formData.criteriaADirectPercent ? parseFloat(formData.criteriaADirectPercent) : 0.0,
      indirect: formData.criteriaAIndirectPercent ? parseFloat(formData.criteriaAIndirectPercent) : 0.0,
      voting_shares: 0.0,
      voting_rights: 0.0,
    });
  }
  if (formData.critboB && (typeBo === 'D' || typeBo === 'I' || typeBo === 'DI')) {
    criteria.push({
      criterion: CRITERION_MAP['B'],
      direct: formData.criteriaBDirectPercent ? parseFloat(formData.criteriaBDirectPercent) : 0.0,
      indirect: formData.criteriaBIndirectPercent ? parseFloat(formData.criteriaBIndirectPercent) : 0.0,
      voting_shares: 0.0,
      voting_rights: 0.0,
    });
  }

  // C (or Joint=Yes): Criteria C/D/E/F checkboxes
  if (typeBo === 'C' || formData.isJointArrangement === 'true') {
    if (formData.critboC) {
      criteria.push({
        criterion: CRITERION_MAP['C'],
        direct: 0.0, indirect: 0.0, voting_shares: 0.0, voting_rights: 0.0,
      });
    }
    if (formData.critboD) {
      criteria.push({
        criterion: CRITERION_MAP['D'],
        direct: 0.0, indirect: 0.0, voting_shares: 0.0, voting_rights: 0.0,
      });
    }
    if (formData.critboE) {
      criteria.push({
        criterion: CRITERION_MAP['E'],
        direct: 0.0, indirect: 0.0, voting_shares: 0.0,
        voting_rights: formData.criteriaEPercent ? parseFloat(formData.criteriaEPercent) : 0.0,
      });
    }
    if (formData.critboF) {
      criteria.push({
        criterion: CRITERION_MAP['F'],
        direct: 0.0, indirect: 0.0,
        voting_shares: formData.criteriaFPercent ? parseFloat(formData.criteriaFPercent) : 0.0,
        voting_rights: 0.0,
      });
    }
  }

  return criteria;
}

export default beneficialOwnerApi;
